<script setup lang="ts">
import { legalCompany as c } from '~/content/legal'
</script>

<template>
    <LegalPage title="Conditions générales de vente"
        description="Conditions générales de vente des prestations de plomberie, chauffage et sanitaire de Deboosere Chauffage Sanitaire.">
        <div class="legal-box">
            <p class="!mb-0">
                <strong>{{ c.name }}</strong> ({{ c.tradeName }}) — {{ c.legalForm }} —
                {{ c.address }} — {{ c.rcs }} — TVA {{ c.vat }} — <a :href="c.phoneHref">{{ c.phone }}</a>
            </p>
        </div>

        <h2>Article 1 — Champ d’application</h2>
        <p>
            Les présentes conditions générales de vente (CGV) s’appliquent à toutes les prestations de dépannage,
            d’entretien, d’installation, de rénovation et de fourniture d’équipements (plomberie, chauffage,
            climatisation, pompes à chaleur, sanitaire) réalisées par {{ c.name }} (ci-après « l’Entreprise »)
            auprès de clients particuliers (« Consommateurs ») ou professionnels (« Clients professionnels »).
        </p>
        <p>
            Elles sont communiquées à tout client qui en fait la demande, accompagnent chaque devis et prévalent sur
            tout autre document, sauf dérogation expresse écrite. Les conditions particulières figurant sur le devis
            prévalent sur les présentes CGV.
        </p>

        <h2>Article 2 — Devis</h2>
        <p>
            Toute prestation fait l’objet d’un devis écrit, détaillé et gratuit, remis avant l’exécution des travaux,
            conformément à l’arrêté du 24 janvier 2017 relatif aux prestations de dépannage, de réparation et
            d’entretien dans le secteur du bâtiment et de l’équipement de la maison. Le devis précise notamment la
            nature et le prix des prestations, le décompte du temps de main-d’œuvre, les frais de déplacement le cas
            échéant, le délai d’exécution et sa durée de validité.
        </p>
        <p>
            Sauf mention contraire, le devis est valable 3 mois à compter de son émission. Au-delà, les prix peuvent
            être révisés.
        </p>
        <p>
            En cas d’intervention d’urgence ne permettant pas l’établissement préalable d’un devis complet, le taux
            horaire, les frais de déplacement et, le cas échéant, le coût du diagnostic sont communiqués au client
            avant toute intervention et acceptés par lui.
        </p>

        <h2>Article 3 — Commande</h2>
        <p>
            La commande est ferme et définitive à la réception par l’Entreprise du devis daté et signé par le client,
            revêtu de la mention « Bon pour accord », accompagné, le cas échéant, de l’acompte prévu au devis.
        </p>
        <p>
            Tous travaux supplémentaires ou modificatifs demandés en cours de chantier font l’objet d’un avenant ou
            d’un devis complémentaire accepté par écrit.
        </p>

        <h2>Article 4 — Droit de rétractation (Consommateurs)</h2>
        <p>
            Lorsque le contrat est conclu à distance ou hors établissement (notamment au domicile du client), le
            Consommateur dispose d’un délai de <strong>14 jours</strong> à compter de la conclusion du contrat pour
            exercer son droit de rétractation, sans avoir à motiver sa décision ni à supporter d’autres coûts que
            ceux prévus par la loi (articles L221-18 et suivants du Code de la consommation).
        </p>
        <p>
            Pour l’exercer, le Consommateur adresse, avant l’expiration du délai, une déclaration dénuée d’ambiguïté
            (par courrier, de préférence recommandé avec accusé de réception) ou le formulaire de rétractation ci-dessous. L’Entreprise rembourse la
            totalité des sommes versées au plus tard 14 jours après avoir été informée de la décision de
            rétractation.
        </p>
        <p>Conformément à l’article L221-28 du Code de la consommation, le droit de rétractation ne peut être exercé :</p>
        <ul>
            <li>
                pour les travaux d’entretien ou de réparation à réaliser en urgence au domicile du Consommateur et
                expressément sollicités par lui, dans la limite des pièces de rechange et travaux strictement
                nécessaires pour répondre à l’urgence ;
            </li>
            <li>
                pour les prestations pleinement exécutées avant la fin du délai de rétractation, dont l’exécution a
                commencé après accord préalable exprès du Consommateur et renoncement exprès à son droit ;
            </li>
            <li>pour la fourniture de biens confectionnés selon les spécifications du Consommateur ou nettement personnalisés.</li>
        </ul>
        <p>
            Si le Consommateur demande expressément que la prestation commence avant la fin du délai de
            rétractation et se rétracte ensuite, il verse un montant proportionnel à ce qui a été fourni jusqu’à la
            communication de sa décision (article L221-25). Aucun paiement ne peut être exigé avant l’expiration
            d’un délai de 7 jours à compter de la conclusion d’un contrat hors établissement, sauf exceptions
            prévues à l’article L221-10 (notamment travaux urgents sollicités par le Consommateur).
        </p>

        <h3>Formulaire de rétractation</h3>
        <div class="legal-box">
            <p>
                <em>(Veuillez compléter et renvoyer le présent formulaire uniquement si vous souhaitez vous
                    rétracter du contrat.)</em>
            </p>
            <p>
                À l’attention de {{ c.name }}, {{ c.address }} :
            </p>
            <p>
                Je/nous (*) vous notifie/notifions (*) par la présente ma/notre (*) rétractation du contrat portant
                sur la prestation de services ci-dessous :
            </p>
            <ul>
                <li>Commandé le (*) / reçu le (*) :</li>
                <li>Nom du (des) consommateur(s) :</li>
                <li>Adresse du (des) consommateur(s) :</li>
                <li>Signature du (des) consommateur(s) (uniquement en cas de notification sur papier) :</li>
                <li>Date :</li>
            </ul>
            <p class="!mb-0">(*) Rayez la mention inutile.</p>
        </div>

        <h2>Article 5 — Prix</h2>
        <p>
            Les prix sont exprimés en euros. Ils s’entendent toutes taxes comprises (TTC) pour les Consommateurs et
            hors taxes (HT) pour les Clients professionnels. Le taux horaire de main-d’œuvre et les frais de
            déplacement sont communiqués avant toute intervention.
        </p>
        <p>
            L’application d’un taux réduit de TVA (10 % ou 5,5 %) pour les travaux dans les logements de plus de
            deux ans est subordonnée au respect des conditions légales et à la remise par le client de l’attestation
            requise. À défaut, ou en cas de déclaration inexacte, le taux normal s’applique et le complément de TVA
            peut être réclamé au client.
        </p>

        <h2>Article 6 — Conditions de paiement</h2>
        <p>
            Un acompte peut être demandé à la commande ; son montant est indiqué sur le devis. Le solde est payable
            à réception de facture, sauf conditions particulières. Les paiements peuvent être effectués par virement,
            chèque, carte bancaire ou espèces dans les limites légales.
        </p>
        <h3>Retard de paiement — Consommateurs</h3>
        <p>
            Toute somme non réglée à l’échéance peut donner lieu, après mise en demeure, à des intérêts de retard au
            taux légal.
        </p>
        <h3>Retard de paiement — Clients professionnels</h3>
        <p>
            Le délai de paiement ne peut dépasser 30 jours à compter de la fin de l’exécution de la prestation, sauf
            accord écrit dans les limites de l’article L441-10 du Code de commerce. Tout retard entraîne de plein
            droit, sans mise en demeure préalable, des pénalités égales à trois fois le taux d’intérêt légal, ainsi
            qu’une indemnité forfaitaire pour frais de recouvrement de 40 € (articles L441-10 et D441-5 du Code de
            commerce). Aucun escompte n’est accordé pour paiement anticipé.
        </p>

        <h2>Article 7 — Réserve de propriété</h2>
        <p>
            Les matériels et équipements fournis restent la propriété de l’Entreprise jusqu’au paiement intégral de
            leur prix, conformément aux articles 2367 et suivants du Code civil. Les risques sont transférés au
            client dès leur livraison ou leur installation.
        </p>

        <h2>Article 8 — Délais et exécution des travaux</h2>
        <p>
            Les délais d’exécution sont indiqués sur le devis. Pour les Consommateurs, en l’absence de délai
            indiqué, les travaux sont réalisés au plus tard 30 jours après la conclusion du contrat (article L216-1
            du Code de la consommation).
        </p>
        <p>
            Le client s’engage à permettre l’accès au chantier, à fournir l’eau et l’électricité nécessaires et à
            signaler toute particularité connue des installations (amiante, canalisations encastrées, etc.). Les
            retards imputables au client, à un tiers, aux intempéries ou à un cas de force majeure ne peuvent
            engager la responsabilité de l’Entreprise.
        </p>

        <h2>Article 9 — Réception des travaux</h2>
        <p>
            À l’achèvement des travaux, la réception est prononcée contradictoirement, avec ou sans réserves, par la
            signature d’un procès-verbal. À défaut, la prise de possession et le paiement du solde valent réception
            sans réserve. Les réserves éventuelles doivent être précises et motivées.
        </p>

        <h2>Article 10 — Garanties</h2>
        <p>Les travaux réalisés bénéficient, selon leur nature, des garanties légales suivantes :</p>
        <ul>
            <li>
                <strong>garantie de parfait achèvement</strong> (1 an à compter de la réception, article 1792-6 du
                Code civil) ;
            </li>
            <li>
                <strong>garantie biennale de bon fonctionnement</strong> des éléments d’équipement dissociables
                (2 ans, article 1792-3 du Code civil) ;
            </li>
            <li>
                <strong>garantie décennale</strong> pour les dommages compromettant la solidité de l’ouvrage ou le
                rendant impropre à sa destination (10 ans, articles 1792 et suivants du Code civil).
            </li>
        </ul>
        <p>
            Pour les biens fournis, le Consommateur bénéficie en outre de la <strong>garantie légale de
                conformité</strong> (articles L217-3 et suivants du Code de la consommation, 2 ans à compter de la
            délivrance du bien) et de la <strong>garantie des vices cachés</strong> (articles 1641 et suivants du
            Code civil, 2 ans à compter de la découverte du vice). Les garanties contractuelles des fabricants
            s’ajoutent à ces garanties légales.
        </p>
        <p>
            Les garanties ne couvrent pas l’usure normale, le défaut d’entretien, une mauvaise utilisation, ni les
            interventions de tiers sur l’installation.
        </p>

        <h2>Article 11 — Assurances</h2>
        <p>
            Conformément à l’article 22-2 de la loi n° 96-603 du 5 juillet 1996, les coordonnées de l’assureur de
            l’Entreprise ainsi que la couverture géographique de son contrat figurent sur chaque devis et
            chaque facture.
        </p>

        <h2>Article 12 — Responsabilité</h2>
        <p>
            L’Entreprise ne saurait être tenue responsable des dommages résultant d’un défaut préexistant de
            l’installation non signalé, de la fourniture par le client de matériel non conforme ou de l’intervention
            d’un tiers. Pour les Clients professionnels, la responsabilité de l’Entreprise, hors garanties légales
            obligatoires, est limitée au montant de la prestation concernée.
        </p>

        <h2>Article 13 — Données personnelles</h2>
        <p>
            Les données collectées sont traitées conformément à notre
            <NuxtLink to="/politique-de-confidentialite">politique de confidentialité</NuxtLink>.
        </p>

        <h2>Article 14 — Réclamations et médiation</h2>
        <p>
            Toute réclamation doit être adressée par écrit à l’Entreprise, par courrier à l’adresse {{ c.address }}.
        </p>
        <p>
            Conformément aux articles L612-1 et suivants du Code de la consommation, le Consommateur peut, en cas
            d’échec de cette réclamation écrite, recourir gratuitement à un médiateur de la consommation. La saisine
            du médiateur doit intervenir dans un délai d’un an à compter de la réclamation écrite.
        </p>

        <h2>Article 15 — Droit applicable et juridiction</h2>
        <p>
            Les présentes CGV sont soumises au droit français. En cas de litige avec un Consommateur, et à défaut de
            solution amiable, les juridictions compétentes sont déterminées selon les règles de droit commun. En cas
            de litige avec un Client professionnel, compétence exclusive est attribuée au tribunal de commerce
            de Lille Métropole.
        </p>
    </LegalPage>
</template>
